import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scale,
  Gavel,
  UserCircle,
  Shield,
  Users,
  BookOpen,
  Briefcase,
  AlertTriangle,
  Search,
  ChevronRight,
  LogIn,
  UserPlus,
  Play,
  Award,
  Landmark,
  Building2,
  GraduationCap,
  Target,
  Trophy,
  Swords,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// صورة الخلفية الاحترافية للمحكمة
const COURT_HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/1c16c9e1-fec7-4786-80e3-da906e2c7780/images/8135b7fb2fd59d19c96e99f4dd4468c2519a413ea1dc3008f1d31e6347fce973.png";
const COURT_HALL_IMAGE = "https://static.prod-images.emergentagent.com/jobs/1c16c9e1-fec7-4786-80e3-da906e2c7780/images/f29da5ce386e8773a9dbe7fa1c93db554f69183433c20f416f032b5a6d6b8bf3.png";

// الأدوار المتاحة في المحكمة الافتراضية
const courtRoles = [
  {
    id: 'judge',
    name: 'قاضي',
    icon: Gavel,
    color: 'from-amber-600 to-amber-800',
    bgColor: 'bg-amber-600',
    description: 'رئاسة الجلسة وإصدار الأحكام',
    features: ['إدارة الجلسات', 'استجواب الأطراف', 'إصدار الأحكام', 'تقييم الأدلة'],
  },
  {
    id: 'prosecutor',
    name: 'محقق نيابة',
    icon: Search,
    color: 'from-red-600 to-red-800',
    bgColor: 'bg-red-600',
    description: 'التحقيق وتمثيل النيابة العامة',
    features: ['التحقيق في الجرائم', 'جمع الأدلة', 'الادعاء العام', 'المرافعة'],
  },
  {
    id: 'arbitrator',
    name: 'محكم',
    icon: Scale,
    color: 'from-purple-600 to-purple-800',
    bgColor: 'bg-purple-600',
    description: 'إدارة قضايا التحكيم والفصل فيها',
    features: ['إضافة أطراف القضية', 'إدارة الجلسات', 'إصدار قرارات التحكيم', 'مراجعة الأدلة'],
  },
  {
    id: 'plaintiff',
    name: 'مدعي',
    icon: UserCircle,
    color: 'from-blue-600 to-blue-800',
    bgColor: 'bg-blue-600',
    description: 'رفع الدعوى والمطالبة بالحقوق',
    features: ['تقديم الدعوى', 'عرض الأدلة', 'المرافعة', 'طلب التعويض'],
  },
  {
    id: 'defendant',
    name: 'مدعى عليه',
    icon: Shield,
    color: 'from-green-600 to-green-800',
    bgColor: 'bg-green-600',
    description: 'الدفاع والرد على الادعاءات',
    features: ['الرد على الدعوى', 'تقديم دفوع', 'عرض البينات', 'الدفاع'],
  },
  {
    id: 'law_student',
    name: 'طالب قانون',
    icon: GraduationCap,
    color: 'from-indigo-600 to-indigo-800',
    bgColor: 'bg-indigo-600',
    description: 'التعلم والتدريب على الإجراءات القانونية',
    features: ['مشاهدة الجلسات', 'التدريب العملي', 'حل التمارين', 'اختبارات تفاعلية'],
  },
  {
    id: 'legal_enthusiast',
    name: 'مهتم بالعمل القانوني',
    icon: Briefcase,
    color: 'from-teal-600 to-teal-800',
    bgColor: 'bg-teal-600',
    description: 'استكشاف عالم القانون والمحاكم',
    features: ['تعلم الإجراءات', 'فهم النظام القضائي', 'محاكاة المحاكمات', 'الألعاب القانونية'],
  },
];

// الميزات الرئيسية للمنصة
const platformFeatures = [
  {
    title: 'المحكمة الافتراضية',
    description: 'محاكاة واقعية لقاعة المحكمة مع منصات لجميع الأطراف',
    icon: Landmark,
    color: 'text-amber-500',
  },
  {
    title: 'منصة التقاضي',
    description: 'نظام متكامل لإدارة القضايا والجلسات',
    icon: Building2,
    color: 'text-blue-500',
  },
  {
    title: 'ذكاء اصطناعي',
    description: 'محاكاة ذكية لأطراف القضية الغائبين',
    icon: Target,
    color: 'text-purple-500',
  },
  {
    title: 'التحدي القانوني',
    description: 'ألعاب تنافسية لاختبار معلوماتك القانونية',
    icon: Trophy,
    color: 'text-green-500',
  },
];

export default function VirtualCourtLanding() {
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    confirmPassword: '',
    role: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('virtualCourtToken');
    const userData = localStorage.getItem('virtualCourtUser');
    if (token && userData) {
      setIsLoggedIn(true);
      setUser(JSON.parse(userData));
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API}/api/virtual-court/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('virtualCourtToken', data.token);
        localStorage.setItem('virtualCourtUser', JSON.stringify(data.user));
        setIsLoggedIn(true);
        setUser(data.user);
        setShowLoginModal(false);
        setLoginForm({ email: '', password: '' });
      } else {
        setError(data.detail || 'خطأ في تسجيل الدخول');
      }
    } catch (err) {
      setError('حدث خطأ في الاتصال');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (registerForm.password !== registerForm.confirmPassword) {
      setError('كلمتا المرور غير متطابقتين');
      setLoading(false);
      return;
    }
    
    if (!registerForm.role) {
      setError('الرجاء اختيار دورك في المنصة');
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/virtual-court/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: registerForm.name,
          email: registerForm.email,
          password: registerForm.password,
          role: registerForm.role,
          phone: registerForm.phone
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('virtualCourtToken', data.token);
        localStorage.setItem('virtualCourtUser', JSON.stringify(data.user));
        setIsLoggedIn(true);
        setUser(data.user);
        setShowRegisterModal(false);
        setRegisterForm({ name: '', email: '', password: '', confirmPassword: '', role: '', phone: '' });
      } else {
        setError(data.detail || 'خطأ في التسجيل');
      }
    } catch (err) {
      setError('حدث خطأ في الاتصال');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('virtualCourtToken');
    localStorage.removeItem('virtualCourtUser');
    setIsLoggedIn(false);
    setUser(null);
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    if (!isLoggedIn) {
      setRegisterForm(prev => ({ ...prev, role: role.id }));
      setShowRegisterModal(true);
    } else {
      // توجيه المستخدم حسب دوره
      navigateToSection(role.id);
    }
  };

  const navigateToSection = (roleId) => {
    switch (roleId) {
      case 'arbitrator':
        navigate('/virtual-court/arbitrator-panel');
        break;
      case 'judge':
        navigate('/virtual-court/courtroom');
        break;
      case 'prosecutor':
        navigate('/virtual-court/prosecutor');
        break;
      case 'plaintiff':
      case 'defendant':
        navigate('/virtual-court/litigation');
        break;
      case 'law_student':
      case 'legal_enthusiast':
        navigate('/virtual-court/training');
        break;
      default:
        navigate('/virtual-court/training');
    }
  };

  return (
    <div className="min-h-screen bg-slate-900" dir="rtl">
      {/* Hero Section */}
      <div 
        className="relative min-h-screen bg-cover bg-center"
        style={{ backgroundImage: `url(${COURT_HERO_IMAGE})` }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/60 to-slate-900"></div>
        
        {/* Navigation */}
        <nav className="relative z-20 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-700 rounded-xl flex items-center justify-center shadow-lg">
                <Scale className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">المحكمة الافتراضية</h1>
                <p className="text-amber-400 text-xs">منصة التدريب القانوني</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {isLoggedIn ? (
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-white font-medium">{user?.name}</p>
                    <p className="text-amber-400 text-sm">{courtRoles.find(r => r.id === user?.role)?.name}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-all"
                  >
                    تسجيل خروج
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className="px-6 py-2.5 text-white border border-white/30 rounded-lg hover:bg-white/10 transition-all flex items-center gap-2"
                  >
                    <LogIn className="w-4 h-4" />
                    دخول
                  </button>
                  <button
                    onClick={() => setShowRegisterModal(true)}
                    className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all flex items-center gap-2 shadow-lg"
                  >
                    <UserPlus className="w-4 h-4" />
                    تسجيل جديد
                  </button>
                </>
              )}
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-32">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full mb-6">
              <Landmark className="w-5 h-5" />
              <span>منصة تدريبية معتمدة</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              المحكمة الافتراضية
              <span className="block text-amber-400 mt-2">للتدريب القانوني</span>
            </h1>
            
            <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
              منصة تفاعلية متكاملة لمحاكاة الإجراءات القضائية وتدريب المحامين والقانونيين
              على الترافع والتقاضي بأحدث التقنيات والذكاء الاصطناعي
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => isLoggedIn ? navigate('/virtual-court/training') : setShowRegisterModal(true)}
                className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-lg font-bold rounded-xl hover:from-amber-600 hover:to-amber-700 transition-all shadow-xl flex items-center gap-3"
                data-testid="start-training-btn"
              >
                <Play className="w-6 h-6" />
                ابدأ التدريب الآن
              </button>
              <button
                onClick={() => navigate('/virtual-court-legacy')}
                className="px-8 py-4 bg-white/10 text-white text-lg font-bold rounded-xl hover:bg-white/20 transition-all border border-white/20 flex items-center gap-3"
                data-testid="challenge-game-btn"
              >
                <Swords className="w-6 h-6" />
                التحدي القانوني
              </button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/30 rounded-full flex items-start justify-center pt-2">
            <div className="w-1.5 h-3 bg-amber-400 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Role Selection Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">اختر دورك في المحكمة</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              سجل الدخول بالصفة التي تناسبك وابدأ تجربة قانونية فريدة
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {courtRoles.map((role) => (
              <div
                key={role.id}
                onClick={() => handleRoleSelect(role)}
                className="group relative bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 hover:border-amber-500/50 transition-all cursor-pointer overflow-hidden"
                data-testid={`role-card-${role.id}`}
              >
                {/* Gradient Background on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${role.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                
                <div className={`w-16 h-16 bg-gradient-to-br ${role.color} rounded-2xl flex items-center justify-center mb-4 shadow-lg transform group-hover:scale-110 transition-transform`}>
                  <role.icon className="w-8 h-8 text-white" />
                </div>
                
                <h3 className="text-xl font-bold text-white mb-2">{role.name}</h3>
                <p className="text-slate-400 text-sm mb-4">{role.description}</p>
                
                <ul className="space-y-2">
                  {role.features.slice(0, 3).map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                      <ChevronRight className="w-4 h-4 text-amber-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <div className="mt-4 pt-4 border-t border-slate-700/50">
                  <span className="flex items-center gap-2 text-amber-400 text-sm font-medium group-hover:text-amber-300">
                    {isLoggedIn ? 'الدخول للمنصة' : 'التسجيل كـ ' + role.name}
                    <ChevronRight className="w-4 h-4 transform group-hover:translate-x-[-4px] transition-transform" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section 
        className="py-20 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${COURT_HALL_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-slate-900/90"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">ميزات المنصة</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              منصة متكاملة توفر تجربة واقعية للإجراءات القضائية
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {platformFeatures.map((feature, idx) => (
              <div
                key={idx}
                className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-amber-500/30 transition-all"
              >
                <feature.icon className={`w-12 h-12 ${feature.color} mb-4`} />
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Access Section for Logged-in Users */}
      {isLoggedIn && (
        <section className="py-16 bg-gradient-to-b from-slate-900 to-slate-800">
          <div className="max-w-7xl mx-auto px-6">
            <div className="bg-gradient-to-br from-amber-600/20 to-amber-800/20 rounded-3xl p-8 border border-amber-500/30">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">مرحباً {user?.name}!</h3>
                  <p className="text-slate-300">أنت مسجل كـ <span className="text-amber-400 font-medium">{courtRoles.find(r => r.id === user?.role)?.name}</span></p>
                </div>
                <div className="flex gap-4">
                  <button
                    onClick={() => navigateToSection(user?.role)}
                    className="px-6 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-all font-medium"
                  >
                    الدخول للوحة التحكم
                  </button>
                  <button
                    onClick={() => navigate('/virtual-court-legacy')}
                    className="px-6 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-all font-medium"
                  >
                    الألعاب والتحديات
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-slate-800 py-8 border-t border-slate-700">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Scale className="w-6 h-6 text-amber-500" />
            <span className="text-white font-bold">المحكمة الافتراضية</span>
          </div>
          <p className="text-slate-400 text-sm">
            منصة تدريبية لمكتب المحامي هشام يوسف الخياط - جميع الحقوق محفوظة © 2024
          </p>
        </div>
      </footer>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-slate-800 rounded-2xl p-8 w-full max-w-md mx-4 border border-slate-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">تسجيل الدخول</h3>
              <button
                onClick={() => { setShowLoginModal(false); setError(''); }}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            {error && (
              <div className="bg-red-500/20 text-red-400 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                {error}
              </div>
            )}
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-slate-300 mb-2">البريد الإلكتروني</label>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <label className="block text-slate-300 mb-2">كلمة المرور</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                  required
                  data-testid="login-password-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 font-bold disabled:opacity-50"
                data-testid="login-submit-btn"
              >
                {loading ? 'جاري التسجيل...' : 'تسجيل الدخول'}
              </button>
            </form>
            
            <p className="text-center text-slate-400 mt-4">
              ليس لديك حساب؟{' '}
              <button
                onClick={() => { setShowLoginModal(false); setShowRegisterModal(true); }}
                className="text-amber-400 hover:text-amber-300"
              >
                سجل الآن
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Register Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8">
          <div className="bg-slate-800 rounded-2xl p-8 w-full max-w-lg mx-4 border border-slate-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">إنشاء حساب جديد</h3>
              <button
                onClick={() => { setShowRegisterModal(false); setError(''); }}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            {error && (
              <div className="bg-red-500/20 text-red-400 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                {error}
              </div>
            )}
            
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-slate-300 mb-2">الاسم الكامل</label>
                <input
                  type="text"
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                  required
                  data-testid="register-name-input"
                />
              </div>
              
              <div>
                <label className="block text-slate-300 mb-2">البريد الإلكتروني</label>
                <input
                  type="email"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                  required
                  data-testid="register-email-input"
                />
              </div>
              
              <div>
                <label className="block text-slate-300 mb-2">رقم الجوال</label>
                <input
                  type="tel"
                  value={registerForm.phone}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                  placeholder="05xxxxxxxx"
                  data-testid="register-phone-input"
                />
              </div>
              
              <div>
                <label className="block text-slate-300 mb-2">اختر دورك في المنصة</label>
                <select
                  value={registerForm.role}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                  required
                  data-testid="register-role-select"
                >
                  <option value="">-- اختر الدور --</option>
                  {courtRoles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-slate-300 mb-2">كلمة المرور</label>
                <input
                  type="password"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
              </div>
              
              <div>
                <label className="block text-slate-300 mb-2">تأكيد كلمة المرور</label>
                <input
                  type="password"
                  value={registerForm.confirmPassword}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 font-bold disabled:opacity-50"
                data-testid="register-submit-btn"
              >
                {loading ? 'جاري التسجيل...' : 'إنشاء الحساب'}
              </button>
            </form>
            
            <p className="text-center text-slate-400 mt-4">
              لديك حساب بالفعل؟{' '}
              <button
                onClick={() => { setShowRegisterModal(false); setShowLoginModal(true); }}
                className="text-amber-400 hover:text-amber-300"
              >
                تسجيل الدخول
              </button>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
